import React from "react";
import { useState, forwardRef, Children, useEffect } from "react";
import { Dropdown, Form, FloatingLabel } from "olive-react-myoneui";

// The forwardRef is important!!
// Dropdown needs access to the DOM node in order to position the Menu
const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <div
    role="button"
    tabIndex={0}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </div>
));

// forwardRef again here!
// Dropdown needs access to the DOM of the Menu to measure it
const CustomMenu = forwardRef(
  (
    {
      children,
      style,
      className,
      "aria-labelledby": labeledBy,
      searchable,
      searchValue,
      setSearchValue,
      onLoadMore,
      hasMore,
      isLoading,
      remoteSearch,
    },
    ref,
  ) => {
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        if (hasMore && !isLoading) {
          onLoadMore();
        }
      }
    };

    return (
      <div
        ref={ref}
        style={{ ...style, maxHeight: "250px", overflowY: "auto" }}
        className={className}
        aria-labelledby={labeledBy}
        onScroll={handleScroll}
      >
        {searchable && (
          <Form.Control
            autoFocus
            className="mx-3 my-2 w-auto"
            placeholder="Type to search..."
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
            style={{ cssText: "width: 90% !important" }}
          />
        )}
        <ul className="list-unstyled mb-0">
          {Children.toArray(children).filter(
            (child) =>
              remoteSearch ||
              !searchValue ||
              child.props.children
                .toLowerCase()
                .includes(searchValue.toLowerCase()),
          )}
        </ul>
      </div>
    );
  },
);

const sampleItems = [];

function DropdownCommon({
  label,
  className,
  items = [],
  defaultValue = null,
  name = "",
  title = "",
  onChange = () => {},
  onSearch = () => {},
  onKeyUp = () => {},
  isLoading,
  searchText,
  onLoadMore,
  hasMore,
  showMoreBtn,
  setSelectedValue,
  selectedValue,
  searchable = false,
  searchValue = "",
  setSearchValue = () => {},
  remoteSearch = false,
}) {
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(null);
  const [scrollHeight, setScrollHeight] = useState(0);
  const listRef = React.useRef(null);
  function handleKeyDown(e) {
    var perHight = 42;
    var sheight = scrollHeight;

    if (e.keyCode === 38) {
      // Up arrow key
      e.preventDefault();
      const index = items.indexOf(selected);
      if (index > 0) {
        setSelected(items[index - 1]);
        if (index > 3) {
          sheight -= perHight;
          setScrollHeight(sheight);
          listRef.current.scrollTo(0, sheight);
        }
      }
    } else if (e.keyCode === 40) {
      // Down arrow key
      e.preventDefault();
      const index = items.indexOf(selected);
      if (index < items.length - 1) {
        setSelected(items[index + 1]);
        if (index > 3) {
          sheight += perHight;
          setScrollHeight(sheight);
          listRef.current.scrollTo(0, sheight);
        }
      }
    } else if (e.keyCode === 13) {
      if (typeof selected === "string" || selected instanceof String) {
        const item = items.filter(
          (item) => item.name.toLowerCase() === selected.toLocaleLowerCase(),
        )[0];
        onChange(item);
        setShow(false);
      } else {
        onChange(selected);
        setShow(false);
      }
    }
  }

  useEffect(() => {
    if (selectedValue) {
      setSelected(selectedValue);
    }
  }, [selectedValue]);
  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);
  const onSelect = (index) => {
    setSelected(items[index]);
    onChange(items[index]);
    setSelectedValue(items[index]);
  };
  const onToggle = (show, meta) => {
    setShow(show);
    if (!show) {
      setSearchValue(""); // Clear search when dropdown closes
    }
  };
  const onInputChange = (e) => {
    // const val = e.target.value ? e.target.value.trim().toLowerCase() : '';
    const { value, name } = e?.target || {};
    setShow(true);
  };
  const onInputKeyChange = (e) => {
    onInputChange(e);
    onKeyUp(e);
  };

  return (
    <Dropdown
      className={`dropdown-select custom-select-dropdown ${className || ""}`}
      onSelect={onSelect}
      show={show}
      onToggle={onToggle}
      // onKeyDown={handleKeyDown}
    >
      <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
        <FloatingLabel
          controlId="floatingSelection"
          label={label ? label : "Choose Option"}
          className="w-100"
        >
          <Form.Control
            autoComplete="off"
            type="text"
            placeholder="Events"
            name={name || title}
            onChange={onInputChange}
            // onKeyUp={onInputKeyChange}
            // onKeyDown={(e) => handleKeyDown(e)}
            value={selected?.name || selected?.title || ""}
          />
          <div className="caret-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="6.119"
              height="11.658"
              viewBox="0 0 6.119 11.658"
            >
              <path
                id="Path_21616"
                data-name="Path 21616"
                d="M0,0,4.42,3.71,8.84,0"
                transform="translate(1.409 10.249) rotate(-90)"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </FloatingLabel>
      </Dropdown.Toggle>
      <Dropdown.Menu
        as={CustomMenu}
        searchable={searchable}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        remoteSearch={remoteSearch}
      >
        {isLoading && !showMoreBtn && (
          <Dropdown.Item className="text-center" disabled>
            Loading...
          </Dropdown.Item>
        )}
        {items?.length
          ? items.map((item, i) => (
              <Dropdown.Item key={i} eventKey={i} active={selected === item}>
                {item.name || item.title}
              </Dropdown.Item>
            ))
          : !isLoading &&
            items.length === 0 && (
              <Dropdown.Item className="text-center" disabled>
                No result found...
              </Dropdown.Item>
            )}
        {showMoreBtn && items.length != 0 && (
          <Dropdown.Item
            disabled={!hasMore}
            className="text-center"
            onClick={(e) => {
              e.stopPropagation();
              onLoadMore();
            }}
          >
            {isLoading ? "Loading ..." : "Load More ..."}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default DropdownCommon;
